import React, { useCallback, useEffect, useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import styled from 'styled-components';
import {
  Button,
  Tooltip as SuperopsTooltip,
  Modal,
  ButtonGroup,
  Page
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import {
  getTranslatedText,
  RunbookNodeActivatorContent
} from '@nile/components';
import { injectIntl, FormattedMessage } from 'react-intl';
import ReplyContainer from '@nile/banyan/src/Containers/CascadeContainers/ReplyContainer';
import { isEmpty } from 'lodash';

const Wrapper = styled.div`
  .Superops-Page--contentPrimary,
  [data-has-navigation] .Superops-Page--contentPrimary {
    padding: 0;
  }
`;

function ReplyRequestorModal(props) {
  const { intl, isAutoAction } = props;

  const saveLoading = false;

  const [allTemplates, setAllTemplates] = useState([]);
  const [selectedReplyTemplate, setSelectedReplyTemplate] = useState(null);

  const formData = props?.autoActionformData || props?.data?.formData || {};
  const actionValueData =
    props?.autoActionformData?.actionValueData || formData?.actionValueData;

  const parentActionId = formData?.parentActionId;
  const hasActionId = formData?.actionId;

  const [popoverActive, setPopoverActive] = useState(
    Boolean(
      (isAutoAction && !props?.autoActionformData) ||
        (!isAutoAction && !hasActionId)
    )
  );

  const showDeleteButton =
    (props.isAutoAction && props?.autoActionformData) ||
    (!props.isAutoAction && hasActionId);

  const togglePopoverActive = useCallback(() => {
    setPopoverActive((active) => !active);
  }, []);

  const activator = (
    <RunbookNodeActivatorContent
      nodeType="admin.runbooks.templateactions.replyrequestor"
      nodeName={
        selectedReplyTemplate?.name ||
        actionValueData?.notificationTemplate?.name ||
        'Select Reply Template'
      }
      iconName="ReplyColoured"
      callBackFunction={togglePopoverActive}
    />
  );

  useEffect(() => {
    if (!isEmpty(actionValueData) && allTemplates.length > 0) {
      const [foundTemplate] = allTemplates.filter(
        (task) => task.id === actionValueData.notificationTemplate?.id
      );
      foundTemplate && setSelectedReplyTemplate(foundTemplate);
    }
  }, [allTemplates, popoverActive]);

  const onSave = () => {
    const formDataTemp = {
      name: formData?.name || '',
      actionType: 'SINGLE',
      actionProcessCategory: props?.isAutoAction ? 'ASYNC' : 'SYNC',
      actionValueType: 'TICKET_REPLY',
      actionValueData: {
        nodeId: props.id,
        notificationTemplate: {
          id: selectedReplyTemplate?.id,
          name: selectedReplyTemplate?.name
        }
      }
    };
    if (hasActionId) {
      formDataTemp.actionId = hasActionId;
    }
    if (parentActionId) {
      formDataTemp.parentActionId = parentActionId;
    }

    if (props.isAutoAction) {
      props.autoActionSave(formDataTemp);
    } else {
      props.modifyNodeData(formDataTemp);
    }
    togglePopoverActive();
  };

  const updateSelectedReplyTemplate = (selectedTemplate) => {
    setSelectedReplyTemplate(selectedTemplate);
  };

  const handleNodeDelete = () => {
    setPopoverActive(false);

    if (props.isAutoAction) {
      props.handleAutoActionDelete(props.id);
    } else {
      props.data.handleNodeDelete(props.id, hasActionId);
    }
  };

  const handleNodeCancel = useCallback(() => {
    setPopoverActive(false);
    if (!props.isAutoAction && !hasActionId) {
      handleNodeDelete();
    } else if (props.isAutoAction && !props?.autoActionformData) {
      props.handleAutoActionDelete(props.id);
    }
  }, [hasActionId, handleNodeDelete]);

  return (
    <Modal
      large
      activator={activator}
      open={popoverActive}
      onClose={togglePopoverActive}
      showCloseButton={false}
      noScroll
    >
      <Page
        title={getTranslatedText(
          intl,
          'admin.runbooks.templateactions.replyrequestor'
        )}
        contentHeight="110px"
        fullWidth
        variant="secondary"
        primaryAction={
          <ButtonGroup spacing="loose">
            {showDeleteButton && (
              <Button plainSecondary onClick={handleNodeDelete}>
                <div className="danger">
                  {getTranslatedText(intl, 'common.delete')}
                </div>
              </Button>
            )}

            <Button plainSecondary onClick={handleNodeCancel}>
              <FormattedMessage id="common.cancel" />
            </Button>

            <Button
              primary
              icon={<SVGIcon name="Save" />}
              onClick={() => {
                onSave();
              }}
              loading={saveLoading}
            >
              <FormattedMessage id="common.save" />
            </Button>
          </ButtonGroup>
        }
      >
        <Wrapper className="min-h80 mt-10">
          <ReplyContainer
            selectedReplyTemplate={selectedReplyTemplate}
            updateSelectedReplyTemplate={updateSelectedReplyTemplate}
            cascadeOnly
            setAllTemplates={setAllTemplates}
            isRunbookTemplate
          />
        </Wrapper>
      </Page>
    </Modal>
  );
}

export default injectIntl(ReplyRequestorModal);
